/**
 * Particle Network Rendering Functions
 */

import { CONFIG, DATA_STREAM_COLORS, PULSE_COLORS } from './config';
import type { Particle, DataPulse, DataStream } from './config';

/**
 * Initialize data streams for flowing wave effect
 */
export function createDataStreams(width: number, height: number): DataStream[] {
	const streams: DataStream[] = [];
	for (let i = 0; i < 3; i++) {
		const stream: DataStream = {
			points: [],
			baseY: height * (0.25 + i * 0.2),
			speed: CONFIG.DATA_STREAM_BASE_SPEED + i * CONFIG.DATA_STREAM_SPEED_INCREMENT,
			amplitude: 40 + i * 20,
			frequency: 0.006 + i * 0.002,
			phase: i * 2,
			color: DATA_STREAM_COLORS[i]
		};
		for (let x = 0; x <= width + 20; x += 4) {
			stream.points.push({ x, y: 0 });
		}
		streams.push(stream);
	}
	return streams;
}

/**
 * Initialize particles across the canvas
 * @param densityMultiplier - Higher values = fewer particles (for mobile performance)
 */
export function createParticles(width: number, height: number, densityMultiplier = 1): Particle[] {
	const particles: Particle[] = [];
	const count = Math.floor((width * height) / (CONFIG.PARTICLE_DENSITY * densityMultiplier));
	for (let i = 0; i < count; i++) {
		const isNode = Math.random() < CONFIG.NODE_PROBABILITY;
		particles.push({
			x: Math.random() * width,
			y: Math.random() * height,
			vx: (Math.random() - 0.5) * CONFIG.INITIAL_VELOCITY,
			vy: (Math.random() - 0.5) * CONFIG.INITIAL_VELOCITY,
			radius: isNode ? Math.random() * 3 + 2 : Math.random() * 2 + 0.5,
			opacity: isNode ? Math.random() * 0.4 + 0.6 : Math.random() * 0.5 + 0.3,
			pulsePhase: Math.random() * Math.PI * 2,
			isNode
		});
	}
	return particles;
}

/**
 * Draw flowing data streams on canvas
 */
export function drawDataStreams(
	ctx: CanvasRenderingContext2D,
	streams: DataStream[],
	width: number,
	height: number,
	time: number
): void {
	streams.forEach((stream) => {
		ctx.beginPath();
		ctx.moveTo(0, height);

		stream.points.forEach((point, i) => {
			const noise =
				Math.sin(time * stream.speed + i * stream.frequency + stream.phase) * stream.amplitude;
			const noise2 =
				Math.sin(time * stream.speed * 0.5 + i * stream.frequency * 2) * stream.amplitude * 0.3;
			point.y = stream.baseY + noise + noise2;

			if (i === 0) {
				ctx.lineTo(point.x, point.y);
			} else {
				const prev = stream.points[i - 1];
				const cpX = (prev.x + point.x) / 2;
				ctx.quadraticCurveTo(prev.x, prev.y, cpX, (prev.y + point.y) / 2);
			}
		});

		ctx.lineTo(width, height);
		ctx.closePath();

		const gradient = ctx.createLinearGradient(0, stream.baseY - stream.amplitude, 0, height);
		gradient.addColorStop(0, stream.color);
		gradient.addColorStop(1, 'transparent');
		ctx.fillStyle = gradient;
		ctx.fill();
	});
}

/**
 * Update particle physics and draw particles
 */
export function updateAndDrawParticles(
	ctx: CanvasRenderingContext2D,
	particles: Particle[],
	width: number,
	height: number,
	mouseX: number,
	mouseY: number,
	time: number
): void {
	particles.forEach((p) => {
		// Mouse attraction (gentle pull towards cursor)
		const dx = mouseX - p.x;
		const dy = mouseY - p.y;
		const dist = Math.sqrt(dx * dx + dy * dy);

		if (dist < CONFIG.MOUSE_ATTRACTION_RADIUS && dist > 0) {
			const force = (CONFIG.MOUSE_ATTRACTION_RADIUS - dist) / CONFIG.MOUSE_ATTRACTION_RADIUS;
			// Attraction when far, repulsion when very close
			if (dist > CONFIG.MOUSE_REPULSION_RADIUS) {
				p.vx += (dx / dist) * force * CONFIG.ATTRACTION_FORCE;
				p.vy += (dy / dist) * force * CONFIG.ATTRACTION_FORCE;
			} else {
				p.vx -= (dx / dist) * force * CONFIG.REPULSION_FORCE;
				p.vy -= (dy / dist) * force * CONFIG.REPULSION_FORCE;
			}
		}

		// Add some gentle random movement
		p.vx += (Math.random() - 0.5) * CONFIG.RANDOM_MOVEMENT;
		p.vy += (Math.random() - 0.5) * CONFIG.RANDOM_MOVEMENT;

		p.x += p.vx;
		p.y += p.vy;

		// Damping
		p.vx *= CONFIG.VELOCITY_DAMPING;
		p.vy *= CONFIG.VELOCITY_DAMPING;

		// Speed limit
		const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
		if (speed > CONFIG.MAX_VELOCITY) {
			p.vx = (p.vx / speed) * CONFIG.MAX_VELOCITY;
			p.vy = (p.vy / speed) * CONFIG.MAX_VELOCITY;
		}

		// Soft bounds (bounce back gently)
		if (p.x < 0) {
			p.x = 0;
			p.vx *= -CONFIG.BOUNCE_DAMPING;
		}
		if (p.x > width) {
			p.x = width;
			p.vx *= -CONFIG.BOUNCE_DAMPING;
		}
		if (p.y < 0) {
			p.y = 0;
			p.vy *= -CONFIG.BOUNCE_DAMPING;
		}
		if (p.y > height) {
			p.y = height;
			p.vy *= -CONFIG.BOUNCE_DAMPING;
		}

		// Pulsing effect for nodes
		const pulse = p.isNode
			? 1 + Math.sin(time * CONFIG.PULSE_FREQUENCY + p.pulsePhase) * CONFIG.PULSE_AMPLITUDE
			: 1;
		const currentRadius = p.radius * pulse;
		const currentOpacity = p.opacity * (0.8 + Math.sin(time * 2 + p.pulsePhase) * 0.2);

		// Draw glow for nodes
		if (p.isNode) {
			const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, currentRadius * 4);
			gradient.addColorStop(0, `rgba(${CONFIG.COLORS.DRESDEN_BLUE}, ${currentOpacity * 0.3})`);
			gradient.addColorStop(1, 'transparent');
			ctx.beginPath();
			ctx.arc(p.x, p.y, currentRadius * 4, 0, Math.PI * 2);
			ctx.fillStyle = gradient;
			ctx.fill();
		}

		// Draw particle
		ctx.beginPath();
		ctx.arc(p.x, p.y, currentRadius, 0, Math.PI * 2);
		ctx.fillStyle = p.isNode
			? `rgba(${CONFIG.COLORS.DRESDEN_BLUE}, ${currentOpacity})`
			: `rgba(255, 255, 255, ${currentOpacity})`;
		ctx.fill();
	});
}

/**
 * Draw connections between nearby particles
 */
export function drawConnections(ctx: CanvasRenderingContext2D, particles: Particle[]): void {
	ctx.lineWidth = 1;
	for (let i = 0; i < particles.length; i++) {
		for (let j = i + 1; j < particles.length; j++) {
			const dx = particles[i].x - particles[j].x;
			const dy = particles[i].y - particles[j].y;
			const dist = Math.sqrt(dx * dx + dy * dy);

			if (dist < CONFIG.CONNECTION_DISTANCE) {
				const alpha = ((CONFIG.CONNECTION_DISTANCE - dist) / CONFIG.CONNECTION_DISTANCE) * 0.4;

				const gradient = ctx.createLinearGradient(
					particles[i].x,
					particles[i].y,
					particles[j].x,
					particles[j].y
				);

				const color1 = particles[i].isNode ? CONFIG.COLORS.DRESDEN_BLUE : CONFIG.COLORS.SLATE;
				const color2 = particles[j].isNode ? CONFIG.COLORS.DRESDEN_BLUE : CONFIG.COLORS.SLATE;

				gradient.addColorStop(0, `rgba(${color1}, ${alpha})`);
				gradient.addColorStop(1, `rgba(${color2}, ${alpha})`);

				ctx.beginPath();
				ctx.moveTo(particles[i].x, particles[i].y);
				ctx.lineTo(particles[j].x, particles[j].y);
				ctx.strokeStyle = gradient;
				ctx.stroke();
			}
		}
	}
}

/**
 * Try to spawn a new data pulse between connected particles
 */
export function trySpawnPulse(particles: Particle[], pulses: DataPulse[]): void {
	for (let attempts = 0; attempts < 10; attempts++) {
		const i = Math.floor(Math.random() * particles.length);
		const j = Math.floor(Math.random() * particles.length);
		if (i !== j) {
			const dx = particles[i].x - particles[j].x;
			const dy = particles[i].y - particles[j].y;
			const dist = Math.sqrt(dx * dx + dy * dy);
			if (dist < CONFIG.PULSE_CONNECTION_DISTANCE) {
				pulses.push({
					fromIndex: i,
					toIndex: j,
					progress: 0,
					speed: CONFIG.PULSE_BASE_SPEED + Math.random() * CONFIG.PULSE_SPEED_VARIANCE,
					color: Math.random() > 0.5 ? PULSE_COLORS[0] : PULSE_COLORS[1]
				});
				break;
			}
		}
	}
}

/**
 * Update and draw data pulses flowing through network
 */
export function updateAndDrawPulses(
	ctx: CanvasRenderingContext2D,
	pulses: DataPulse[],
	particles: Particle[]
): DataPulse[] {
	return pulses.filter((pulse) => {
		pulse.progress += pulse.speed;

		if (pulse.progress >= 1) return false;

		const from = particles[pulse.fromIndex];
		const to = particles[pulse.toIndex];

		if (!from || !to) return false;

		const x = from.x + (to.x - from.x) * pulse.progress;
		const y = from.y + (to.y - from.y) * pulse.progress;

		// Draw pulse with glow
		const glowGradient = ctx.createRadialGradient(x, y, 0, x, y, CONFIG.PULSE_GLOW_RADIUS);
		glowGradient.addColorStop(0, pulse.color);
		glowGradient.addColorStop(1, 'transparent');

		ctx.beginPath();
		ctx.arc(x, y, CONFIG.PULSE_GLOW_RADIUS, 0, Math.PI * 2);
		ctx.fillStyle = glowGradient;
		ctx.fill();

		ctx.beginPath();
		ctx.arc(x, y, CONFIG.PULSE_CORE_RADIUS, 0, Math.PI * 2);
		ctx.fillStyle = 'white';
		ctx.fill();

		return true;
	});
}

/**
 * Draw mouse glow effect
 */
export function drawMouseGlow(ctx: CanvasRenderingContext2D, mouseX: number, mouseY: number): void {
	if (mouseX > 0 && mouseY > 0) {
		const mouseGradient = ctx.createRadialGradient(
			mouseX,
			mouseY,
			0,
			mouseX,
			mouseY,
			CONFIG.MOUSE_GLOW_RADIUS
		);
		mouseGradient.addColorStop(0, `rgba(${CONFIG.COLORS.DRESDEN_BLUE}, 0.15)`);
		mouseGradient.addColorStop(0.5, `rgba(${CONFIG.COLORS.DRESDEN_BLUE}, 0.05)`);
		mouseGradient.addColorStop(1, 'transparent');

		ctx.beginPath();
		ctx.arc(mouseX, mouseY, CONFIG.MOUSE_GLOW_RADIUS, 0, Math.PI * 2);
		ctx.fillStyle = mouseGradient;
		ctx.fill();
	}
}

/**
 * Draw floating ambient orbs in background
 */
export function drawAmbientOrbs(
	ctx: CanvasRenderingContext2D,
	width: number,
	height: number,
	time: number
): void {
	for (let i = 0; i < 4; i++) {
		const orbX = width * (0.2 + i * 0.2) + Math.sin(time * 0.5 + i * 1.5) * 80;
		const orbY = height * 0.4 + Math.cos(time * 0.4 + i * 2) * 100;
		const orbRadius = 120 + Math.sin(time * 0.3 + i) * 40;

		const orbGradient = ctx.createRadialGradient(orbX, orbY, 0, orbX, orbY, orbRadius);
		orbGradient.addColorStop(0, `rgba(${CONFIG.COLORS.DRESDEN_BLUE}, 0.08)`);
		orbGradient.addColorStop(0.5, `rgba(${CONFIG.COLORS.PARIS_GREEN}, 0.04)`);
		orbGradient.addColorStop(1, 'transparent');

		ctx.beginPath();
		ctx.arc(orbX, orbY, orbRadius, 0, Math.PI * 2);
		ctx.fillStyle = orbGradient;
		ctx.fill();
	}
}
